import React, { useContext, createContext, useEffect, useState } from 'react';
import { API_ENDPOINTS, api, apiCall } from '../config/api';
export interface RawUser {
  user_id: number;
  email: string;
  role: string;
  created_at: string;
  updated_at: string;
}

export interface User {
  userId: number;
  email: string;
  role: string;
  createdAt: string;
  updatedAt: string;
}

function mapUser(raw: RawUser): User {
  return {
    userId: raw.user_id,
    email: raw.email,
    role: raw.role,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  };
}

export interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // ✅ Initialize token from localStorage immediately
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('authToken'));
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing token on mount
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('authToken');
      
      if (storedToken) {
        setToken(storedToken);
        await verifyToken(storedToken);
      } else {
        setIsLoading(false);
      }
    };

    initAuth();
  }, []);

  const verifyToken = async (tokenString: string) => {
     try {
      const raw = await apiCall<RawUser>(API_ENDPOINTS.user.me, {
        headers: { Authorization: `Bearer ${tokenString}` },
      });

        const mappedUser = mapUser(raw as RawUser);
        setUser(mappedUser);
        setToken(tokenString);
    } catch (err) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('individualID');
        setToken(null);
        setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

 const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const data = await api.post<{ token: string; user: RawUser }>(
        API_ENDPOINTS.auth.login,
        { email, password }
      );

      console.log('[AUTH] Login response received');
      const mappedUser = mapUser(data.user);
      
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('individualID', mappedUser.userId.toString());
      
      setToken(data.token);
      setUser(mappedUser);
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const data = await api.post<{ token: string; user: RawUser }>(
        API_ENDPOINTS.auth.register,
        { email, password }
      );

      const mappedUser = mapUser(data.user);
      
      localStorage.setItem('authToken', data.token);
      localStorage.setItem('individualID', mappedUser.userId.toString());
      
      setToken(data.token);
      setUser(mappedUser);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('individualID');
    localStorage.removeItem('authFlow');
    localStorage.removeItem('userType');
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated: !!token, // ✅ This will now be true immediately on refresh
    login,
    register,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};